import { useState } from "react";
import { adminApi, formatApiError } from "../../lib/adminApi";
import { KindredMark } from "../KindredMark";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const inputClass = "h-12 rounded-full border-cream/20 bg-cream/5 px-5 text-cream placeholder:text-cream/40 focus-visible:ring-2 focus-visible:ring-gold";
const submitClass = "h-12 w-full rounded-full bg-gold text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-200 hover:bg-gold-deep active:scale-95";
const linkClass = "text-xs uppercase tracking-[0.15em] text-cream/40 transition-colors duration-300 hover:text-gold";

const ForgotForm = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await adminApi.post("/auth/forgot-password", { email });
      setState("sent");
    } catch (err) {
      setState("error:" + formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="admin-forgot-form">
      <p className="text-center text-sm leading-relaxed text-cream/60">
        Enter your admin email and we'll send you a reset link — good for one hour.
      </p>
      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" aria-label="Admin email" data-testid="admin-forgot-email-input" className={inputClass} />
      {state === "sent" && (
        <p className="text-sm font-medium text-gold" data-testid="admin-forgot-success">If that email belongs to an admin, a reset link is on its way.</p>
      )}
      {state.startsWith("error:") && (
        <p className="text-sm font-medium text-red-400" data-testid="admin-forgot-error">{state.slice(6)}</p>
      )}
      <Button type="submit" disabled={state === "loading"} data-testid="admin-forgot-submit" className={submitClass}>
        {state === "loading" ? "Sending…" : "Send reset link"}
      </Button>
      <p className="text-center">
        <button type="button" onClick={onBack} data-testid="admin-forgot-back" className={linkClass}>Back to sign in</button>
      </p>
    </form>
  );
};

export const AdminLoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const message = await onLogin(email, password);
    if (message) setError(message);
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-obsidian px-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="mb-10 text-center">
          <KindredMark className="mx-auto mb-4 h-14 w-14" />
          <h1 className="font-serif text-3xl tracking-tight text-cream">Community Hearts</h1>
          <p className="mt-2 text-xs font-bold uppercase tracking-[0.25em] text-gold">Admin access</p>
        </div>
        {forgotMode ? (
          <ForgotForm onBack={() => setForgotMode(false)} />
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" aria-label="Admin email" data-testid="admin-email-input" className={inputClass} />
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" aria-label="Password" data-testid="admin-password-input" className={inputClass} />
            {error && <p className="text-sm font-medium text-red-400" data-testid="admin-login-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="admin-login-button" className={submitClass}>
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        )}
        <p className="mt-5 text-center">
          {!forgotMode && (
            <button onClick={() => setForgotMode(true)} data-testid="admin-forgot-link" className={linkClass}>Forgot password?</button>
          )}
        </p>
        <p className="mt-4 text-center text-xs text-cream/30">
          <a href="/" className="transition-colors duration-300 hover:text-cream" data-testid="admin-back-to-site-link">Back to the site</a>
        </p>
      </div>
    </div>
  );
};
